import type { RootStore } from 'logic';
import { Route, RouterState, RouterStore } from 'mobx-state-router';

export enum RouteNames {
  HOME = 'home',
  PROJECTS = 'projects',
  IMPORT_CSV = 'importcsv',
  IMPORT_LIST = 'importlist',
  IMPORT_SPREADSHEET = 'importspreadsheet',
  IMPORT_IMAGES = 'importimages',
  IMPORT_PDF = 'importpdf',
  TOUR = 'tour',
  ONBOARDING_PROJECT_INFO = 'onboarding-project-info',
  ONBOARDING_SOURCE_SELECTION = 'onboarding-source-selection',
  ONBOARDING_DATA_UPLOAD = 'onboarding-data-upload',
  SESSION_HOME = 'session-home',
  SESSION_SEARCH = 'session-search',
  SESSION_HOME_SOURCE = 'session-home-source',
  SESSION_QUEUE = 'session-queue',
  SESSION_QUEUE_LEGACY = 'session-queue-legacy',
  SESSION_STEP = 'session-step',
  SESSION_DASHBOARD = 'session-dashboard',
  SESSION_MILESTONES = 'session-milestones',
  SESSION_NOTES = 'session-notes',
  SESSION_FIND = 'session-find',
  SESSION_LIBRARY = 'session-library',
  SESSION_LIBRARY_LEGACY = 'session-library-legacy',
  SESSION_ASSETS = 'session-assets',
  SESSION_WHOLE = 'session-whole',
  CARD = 'card',
  // Legacy route names for backward compatibility
  SESSION_LEGACY = 'session-legacy',
  SESSION_DASHBOARD_LEGACY = 'session-dashboard-legacy',
  SESSION_MILESTONES_LEGACY = 'session-milestones-legacy',
  SESSION_NOTES_LEGACY = 'session-notes-legacy',
  CARD_LEGACY = 'card-legacy',
  // Organization and Admin routes
  ORGANIZATION_ADMIN = 'organizationAdmin',
  CQ_ADMIN = 'cqAdmin',
  CQ_ADMIN_ORGANIZATIONS = 'cqAdminOrganizations',
  CQ_ADMIN_USER_STATUS = 'cqAdminUserStatus',
  CQ_ADMIN_USER_STATUS_INVITATIONS = 'cqAdminUserStatusInvitations',
  CQ_ADMIN_USERS = 'cqAdminUsers',
  CQ_ADMIN_USAGE_LIMITS = 'cqAdminUsageLimits',
  CQ_ADMIN_CQ_ADMINS = 'cqAdminCQAdmins',
  CQ_ADMIN_TROUBLESHOOTING = 'cqAdminTroubleshooting',
  CQ_ADMIN_AUDIT_LOGS = 'cqAdminAuditLogs',
  CQ_ADMIN_SESSION_SHARE_TOKENS = 'cqAdminSessionShareTokens',
  CQ_ADMIN_ORG_DETAILS = 'cqAdminOrgDetails',
  CQ_ADMIN_ORG_MEMBERSHIP = 'cqAdminOrgMembership',
  CQ_ADMIN_ORG_SESSIONS = 'cqAdminOrgSessions',
  SESSION_SHARE = 'sessionShare',
  CLAIM_INVITATION = 'claimInvitation',
  APPROVE_OWNERSHIP_TRANSFER = 'approveOwnershipTransfer',
}

export const routes: Route[] = [
  {
    name: RouteNames.HOME,
    pattern: '/',
  },
  {
    name: RouteNames.PROJECTS,
    pattern: '/projects',
  },
  {
    name: RouteNames.IMPORT_CSV,
    pattern: '/import/csv',
  },
  {
    name: RouteNames.IMPORT_LIST,
    pattern: '/import/list',
  },
  {
    name: RouteNames.IMPORT_SPREADSHEET,
    pattern: '/import/custom',
  },
  {
    name: RouteNames.IMPORT_IMAGES,
    pattern: '/import/images',
  },
  {
    name: RouteNames.IMPORT_PDF,
    pattern: '/import/pdf',
  },
  {
    name: RouteNames.TOUR,
    pattern: '/tour',
  },
  {
    name: RouteNames.ONBOARDING_PROJECT_INFO,
    pattern: '/new',
  },
  {
    name: RouteNames.ONBOARDING_SOURCE_SELECTION,
    pattern: '/new/buckets',
  },
  {
    name: RouteNames.ONBOARDING_DATA_UPLOAD,
    pattern: '/new/upload',
  },
  {
    /*
      Data loading and watching for this route happens as reactions in stores:
        - When user is changed, sessions for user are watched in sessionsStore
        - When sessionsStore's currentSession changes:
          - cardsStore watches cardList for current session
          - commentsStore watches comments for current session
          - sourcesStore watches sources for current session
            - each source watches its rows collection
    */
    name: RouteNames.SESSION_HOME,
    pattern: '/project/:sessionId',
  },
  {
    name: RouteNames.SESSION_SEARCH,
    pattern: '/project/:sessionId/search',
  },
  {
    name: RouteNames.SESSION_QUEUE,
    // stepName deprecated, kept for URL compatibility with bookmarked/shared links
    pattern: '/project/:sessionId/cards/:stepId?/:stepName?',
  },
  {
    // Legacy route - redirects to SESSION_QUEUE
    name: RouteNames.SESSION_QUEUE_LEGACY,
    // stepName deprecated, kept for URL compatibility with bookmarked/shared links
    pattern: '/project/:sessionId/queue/:stepId?/:stepName?',
  },
  {
    // Alias route - redirects to SESSION_QUEUE
    name: RouteNames.SESSION_STEP,
    // stepName deprecated, kept for URL compatibility with bookmarked/shared links
    pattern: '/project/:sessionId/step/:stepId/:stepName?',
  },
  {
    name: RouteNames.SESSION_DASHBOARD,
    pattern: '/project/:sessionId/dashboard',
  },
  {
    name: RouteNames.SESSION_MILESTONES,
    pattern: '/project/:sessionId/milestones',
  },
  {
    name: RouteNames.SESSION_NOTES,
    pattern: '/project/:sessionId/notes',
  },
  {
    name: RouteNames.SESSION_FIND,
    pattern: '/project/:sessionId/find',
  },
  {
    name: RouteNames.SESSION_LIBRARY,
    pattern: '/project/:sessionId/sources/:sourceId?',
  },
  {
    // Legacy route - redirects to SESSION_LIBRARY
    name: RouteNames.SESSION_LIBRARY_LEGACY,
    pattern: '/project/:sessionId/library/:sourceId?',
  },
  {
    name: RouteNames.SESSION_ASSETS,
    pattern: '/project/:sessionId/assets/:stepId?',
  },
  {
    name: RouteNames.SESSION_WHOLE,
    pattern: '/project/:sessionId/whole',
  },
  {
    name: RouteNames.CARD,
    pattern: '/project/:sessionId/card/:cardId',
  },
  {
    name: RouteNames.SESSION_HOME_SOURCE,
    pattern: '/project/:sessionId/home/:sourceId?',
  },
  {
    /*
      Legacy routes for backward compatibility - these will redirect to /project/... routes
    */
    name: RouteNames.SESSION_LEGACY,
    pattern: '/session/:sessionId',
  },
  {
    name: RouteNames.SESSION_DASHBOARD_LEGACY,
    pattern: '/session/:sessionId/dashboard',
  },
  {
    name: RouteNames.SESSION_MILESTONES_LEGACY,
    pattern: '/session/:sessionId/milestones',
  },
  {
    name: RouteNames.SESSION_NOTES_LEGACY,
    pattern: '/session/:sessionId/notes',
  },
  {
    name: RouteNames.CARD_LEGACY,
    pattern: '/session/:sessionId/card/:cardId',
  },
  {
    name: RouteNames.ORGANIZATION_ADMIN,
    pattern: '/organizationAdmin',
  },
  {
    name: RouteNames.CQ_ADMIN,
    pattern: '/admin',
  },
  {
    name: RouteNames.CQ_ADMIN_ORGANIZATIONS,
    pattern: '/admin/organizations',
  },
  {
    name: RouteNames.CQ_ADMIN_USER_STATUS,
    pattern: '/admin/user-status',
  },
  {
    name: RouteNames.CQ_ADMIN_USER_STATUS_INVITATIONS,
    pattern: '/admin/user-status/user-invitations',
  },
  {
    name: RouteNames.CQ_ADMIN_USERS,
    pattern: '/admin/users',
  },
  {
    name: RouteNames.CQ_ADMIN_USAGE_LIMITS,
    pattern: '/admin/usage-limits',
  },
  {
    name: RouteNames.CQ_ADMIN_CQ_ADMINS,
    pattern: '/admin/cq-admins',
  },
  {
    name: RouteNames.CQ_ADMIN_TROUBLESHOOTING,
    pattern: '/admin/troubleshooting',
  },
  {
    name: RouteNames.CQ_ADMIN_AUDIT_LOGS,
    pattern: '/admin/auditLogs',
  },
  {
    name: RouteNames.CQ_ADMIN_SESSION_SHARE_TOKENS,
    pattern: '/admin/session-share-tokens',
  },
  {
    name: RouteNames.CQ_ADMIN_ORG_DETAILS,
    pattern: '/admin/organization-details/:organizationId',
  },
  {
    name: RouteNames.CQ_ADMIN_ORG_MEMBERSHIP,
    pattern: '/admin/organization-details/:organizationId/membership-management',
  },
  {
    name: RouteNames.CQ_ADMIN_ORG_SESSIONS,
    pattern: '/admin/organization-details/:organizationId/session-management',
  },
  {
    name: RouteNames.SESSION_SHARE,
    pattern: '/project/:sessionId/share/:shareToken',
  },
  {
    name: RouteNames.CLAIM_INVITATION,
    pattern: '/claim/:token',
  },
  {
    name: RouteNames.APPROVE_OWNERSHIP_TRANSFER,
    pattern: '/approve-ownership-transfer',
  },
];

export class UiRouterStore extends RouterStore {
  constructor(rootStore: RootStore, routerRoutes: Route[], notFoundState: RouterState) {
    super(routerRoutes, notFoundState, { rootStore });
  }
  public goTo(
    path: RouteNames,
    options?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    },
  ) {
    return super.goTo(path, options);
  }
}
